import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { billingApi } from '../services/billing-api';
import { toast } from 'sonner';

export function useSubscriptionStatus() {
  return useQuery({
    queryKey: ['subscription-status'],
    queryFn: () => billingApi.getStatus(),
    staleTime: 60 * 1000, // 1 min — nu e nevoie de refresh constant
  });
}

/** Oferta «firma noua» — face lookup ANAF pe server; staleTime mare, pagina /preturi. */
export function useNewCompanyOffer(enabled = true) {
  return useQuery({
    queryKey: ['new-company-offer'],
    queryFn: () => billingApi.getNewCompanyOffer(),
    staleTime: 10 * 60 * 1000,
    enabled,
  });
}

export function useClaimNewCompanyOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => billingApi.claimNewCompanyOffer(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['new-company-offer'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
    },
  });
}

export function useCatalog() {
  return useQuery({
    queryKey: ['subscription-catalog'],
    queryFn: () => billingApi.getCatalog(),
    staleTime: 60 * 60 * 1000, // 1h — catalogul se schimba rar
  });
}

export function useCanIssueInvoice() {
  return useQuery({
    queryKey: ['can-issue-invoice'],
    queryFn: () => billingApi.canIssueInvoice(),
    staleTime: 30 * 1000, // 30s — se invalideaza dupa fiecare emitere
  });
}

export function useCanRecordExpense() {
  return useQuery({
    queryKey: ['can-record-expense'],
    queryFn: () => billingApi.canRecordExpense(),
    staleTime: 30 * 1000,
  });
}

export function useActivateModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (moduleCode: string) => billingApi.activateModule(moduleCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscription-status'] });
      qc.invalidateQueries({ queryKey: ['can-issue-invoice'] });
      qc.invalidateQueries({ queryKey: ['can-record-expense'] });
      toast.success('Modulul a fost activat');
    },
    onError: () => toast.error('Eroare la activarea modulului'),
  });
}

export function useCreateCheckout() {
  return useMutation({
    mutationFn: (params: { modules?: string[]; bundleCode?: string } | string[]) => billingApi.createCheckout(Array.isArray(params) ? { modules: params } : params),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (err: any) => toast.error(err?.message || 'Eroare la crearea sesiunii de plata'),
  });
}

/**
 * Plată prin transfer bancar — generează proforma BWLS cu termen 7 zile,
 * trimite email cu PDF + IBAN BEST ON WEB SRL. NO Stripe redirect — confirm
 * page-side cu detalii proforma (modulele și suma).
 */
export function useCreateProformaBankTransfer() {
  return useMutation({
    mutationFn: (modules: string[]) => billingApi.createProformaBankTransfer(modules),
    onError: (err: any) => toast.error(err?.message || 'Eroare la generarea proformei'),
  });
}

export function useCreatePortal() {
  return useMutation({
    mutationFn: () => billingApi.createPortal(),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (err: any) => toast.error(err?.message || 'Eroare la deschiderea portalului de plata'),
  });
}

export function useSetupCard() {
  return useMutation({
    mutationFn: () => billingApi.setupCard(),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (err: any) => toast.error(err?.message || 'Eroare la inițierea salvării cardului'),
  });
}

export function useInvoiceHistory() {
  return useQuery({
    queryKey: ["subscription-invoices"],
    queryFn: () => billingApi.listInvoices(),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePaymentMethod() {
  return useQuery({
    queryKey: ["subscription-payment-method"],
    queryFn: () => billingApi.getPaymentMethod(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSetAutoRenew() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => billingApi.setAutoRenew(enabled),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['subscription-status'] });
      toast.success(data.autoRenew ? 'Reînnoire automată reactivată' : 'Reînnoire automată anulată');
    },
    onError: (err: any) => toast.error(err?.message || 'Eroare la actualizarea reînnoirii'),
  });
}
